/**
 * Game mechanics / keyword definitions for the Codex.
 * Each entry describes a mechanical keyword that appears on abilities,
 * status effects, or system descriptions.
 *
 * Language rules:
 *   "increased" = additive bucket (passive gear/mastery). Diminishing returns.
 *   "more"      = multiplicative bucket (active combat buffs/debuffs). Stacks additively within bucket.
 *   "less"      = negative multiplier (e.g. Weakened).
 */

export interface MechanicDef {
  id: string;
  name: string;
  category: 'tag' | 'system' | 'stat';
  description: string;
}

export const MECHANICS: MechanicDef[] = [
  // ── Ability Tags ─────────────────────────────────────────
  {
    id: 'attack',
    name: 'Attack',
    category: 'tag',
    description: 'Physical strikes — swords, fists, arrows, daggers. Attack-tagged abilities benefit from Rallied (+10% more damage) and "+X% increased Attack damage" on gear.',
  },
  {
    id: 'spell',
    name: 'Spell',
    category: 'tag',
    description: 'Magical abilities cast at range. Spell-tagged abilities benefit from "+X% increased Spell damage" on gear. Most elemental abilities are spells.',
  },
  {
    id: 'melee',
    name: 'Melee',
    category: 'tag',
    description: 'Close-range attacks. Benefits from "+X% increased Melee damage" on gear.',
  },
  {
    id: 'ranged',
    name: 'Ranged',
    category: 'tag',
    description: 'Attacks that strike from a distance — bows, guns, thrown weapons, and targeted spells. Benefits from "+X% increased Ranged damage" on gear.',
  },
  {
    id: 'projectile',
    name: 'Projectile',
    category: 'tag',
    description: 'Abilities that fire a projectile at the target. Projectile abilities deal more damage to Marked targets. Benefits from "+X% increased Projectile damage" on gear.',
  },
  {
    id: 'aoe',
    name: 'AoE',
    category: 'tag',
    description: 'Area of effect — hits multiple targets. Benefits from "+X% increased AoE damage" on gear. Some hit all enemies, others hit adjacent targets (like Cleave).',
  },
  {
    id: 'impact',
    name: 'Impact',
    category: 'tag',
    description: 'Heavy, committal attacks. Impact abilities deal more damage to Fractured targets. Benefits from "+X% increased Impact damage" on gear. Examples: Thunderstrike, Power Shot, Smash, Demolish.',
  },
  {
    id: 'exploit',
    name: 'Exploit',
    category: 'tag',
    description: 'Attacks that capitalize on openings. Exploit abilities deal more damage to Exposed targets. Examples: Backstab, Precise Strike.',
  },
  {
    id: 'dot',
    name: 'DoT',
    category: 'tag',
    description: 'Damage over time — abilities that apply persistent effects like Burn, Bleed, or Poison. Benefits from "+X% increased DoT damage" on gear and DoT Potency stats.',
  },
  {
    id: 'charged',
    name: 'Charged',
    category: 'tag',
    description: 'Abilities with a cast time — you commit a turn to charge, then the ability fires next turn. Higher risk, higher reward. Benefits from "+X% increased Charged damage" on gear.',
  },
  {
    id: 'tempo',
    name: 'Tempo',
    category: 'tag',
    description: 'Fast attacks that build Tempo stacks on the target. Tempo stacks are consumed by spender abilities for bonus damage.',
  },
  {
    id: 'shout',
    name: 'Shout',
    category: 'tag',
    description: 'Battle cries and commands that buff allies or debuff enemies. Shout abilities come from the Valor mastery.',
  },

  // ── Systems ──────────────────────────────────────────────
  {
    id: 'tempo_system',
    name: 'Tempo (System)',
    category: 'system',
    description: 'A builder/spender resource. Fast attacks build Tempo stacks on the target. Spender abilities consume stacks for bonus damage per stack. Subterfuge is the home mastery, but builders exist across multiple masteries.',
  },
  {
    id: 'fervor_system',
    name: 'Fervor (System)',
    category: 'system',
    description: 'A party-wide resource. Fervor stacks are generated by kills, critical hits, and specific abilities. Spender abilities consume Fervor for bonus damage. Valor is the home mastery.',
  },
  {
    id: 'fractured_system',
    name: 'Fractured',
    category: 'system',
    description: 'An amplifier debuff for heavy builds. Fractured targets take more damage from the next Impact ability, then Fractured is consumed. Applied by Lithurgy and Macecraft abilities.',
  },
  {
    id: 'exposed_system',
    name: 'Exposed',
    category: 'system',
    description: 'An amplifier debuff for fast builds. Exposed targets take more damage from the next Exploit ability, then Exposed is consumed. Applied by Tactics (Analyze).',
  },
  {
    id: 'marked_system',
    name: 'Marked',
    category: 'system',
    description: 'An amplifier debuff for ranged builds. Marked targets take more damage from Projectile abilities. Applied by Ballistics (Hunter\'s Mark) and Tactics (Designate).',
  },
  {
    id: 'barrier_system',
    name: 'Barrier',
    category: 'system',
    description: 'A separate absorb pool that takes damage before HP. Multiple Barrier sources stack as independent instances — each tracks its own amount and duration. When you take damage, the Barrier with the shortest remaining duration drains first, preserving your longer-lasting protection. Barriers expire when their duration runs out OR when fully absorbed. Hydromancy (Brinewall, Tideguard) is the primary Barrier mastery; Bulwark provides Iron Wall. Gear can roll Barrier on Hit, Barrier on Crit, +% increased Barrier, +Barrier Duration, and conditional bonuses while a Barrier is active (resistance, damage reduction, increased damage). Hydromancy mastery grants +2% Barrier Potency per rank.',
  },
  {
    id: 'boon_system',
    name: 'Boon',
    category: 'system',
    description: 'A standard self/ally buff — Speed Up, Fury, Acuity, Poised, Taunt, etc. Boons are any buff that isn\'t a Barrier or Regen, and are the default buff category. Boon Potency affixes amplify the size of stat changes (e.g., +Damage from Fury, +Speed from Haste). Buff Duration affixes extend their turn count. Boons strip off when cleansed by enemy abilities targeting buffs.',
  },
  {
    id: 'regen_system',
    name: 'Regen',
    category: 'system',
    description: 'A heal-over-time buff that restores HP at the start of the afflicted unit\'s turn. The heal amount is snapshotted at apply time — Healing Done (caster) and Healing Received (target) affixes are baked in then, not on each tick. Same-type Regens refresh duration and take the higher heal value (no stacking). Verdancy (Nature Regen, Tonic) is the primary Regen mastery. Regens strip off when cleansed as a buff.',
  },
  {
    id: 'hex_system',
    name: 'Hex',
    category: 'system',
    description: 'A debuff that reduces the target\'s output — less damage dealt (Weakened), less healing received (Smothered), and similar effectiveness penalties. Hex Potency affixes amplify the size of the penalty. Hexes are the defensive half of the debuff axis (lower their numbers) — paired with Ruins (raise your damage against them). Hexes strip off when cleansed as a debuff.',
  },
  {
    id: 'ruin_system',
    name: 'Ruin',
    category: 'system',
    description: 'A debuff that increases damage taken by the target — Hunter\'s Mark (+25% Projectile), Fractured (+50% Impact), Exposed (+30% Tempo), and similar vulnerability amplifiers. Ruin Potency affixes amplify the size of the vulnerability. Ruins are the offensive half of the debuff axis (raise your damage against them) — paired with Hexes (lower their output). Ruins live in Pool 3 (Target Vulnerability) — see the Three Damage Pools entry.',
  },

  // ── Key Stats ────────────────────────────────────────────
  {
    id: 'increased_damage',
    name: 'Increased Damage',
    category: 'stat',
    description: 'All sources of "increased" damage add together into one pool — gear affixes, tagIncreased mastery ranks (Spellcraft, Macecraft, Ballistics, etc.), conditional sources (while a Barrier is active, against Wounded enemies, against Debuffed enemies, against Full Health enemies), and STR Up / INT Up enemy buffs. Applied as one multiplier on the damage line. Stacking more "increased" gives diminishing returns: each new source dilutes against the growing pool. Example: 40% increased Fire + 20% increased Spell + 30% against Debuffed = 90% increased total against a debuffed fire target. Target-state amplifier debuffs (Marked, Fractured, Exposed, Broken) also read as "increased Damage" in their tooltips by ARPG convention, even though they live in a separate multiplicative bucket — the keyword name (Marked/Fractured/etc.) signals which bucket the bonus lives in.',
  },
  {
    id: 'more_damage',
    name: 'More Damage',
    category: 'stat',
    description: '"More" damage is reserved for the small set of attacker-side multiplicative buffs: Empower, Fury, Vanish, Rallied, First Strike. Each "more" source is summed into one pool, then that pool multiplies the damage line *after* the increased pool — so "more" is strictly stronger per-percent than "increased" because it skips dilution. This is why "more" mods are rare and short-lived: a 20% Empower is a real 1.20× multiplier on your next hit, not diluted by your gear pool. Target-state amplifier debuffs (Marked, Fractured, Broken, etc.) are mechanically multiplicative too, but read as "increased" in their tooltips — the bucket-identity is carried by the keyword name, not the numeric word.',
  },
  {
    id: 'less_damage',
    name: 'Less Damage',
    category: 'stat',
    description: '"Less" damage reduces your output. Currently only Weakened applies this. It multiplies independently — a Weakened attacker deals 20% less damage regardless of other bonuses.',
  },
  {
    id: 'effectiveness',
    name: 'Effectiveness',
    category: 'stat',
    description: 'How much of your weapon\'s flat damage an ability uses. An ability with 60% Effectiveness only benefits from 60% of your flat gear damage. High-base-damage abilities often have low effectiveness to prevent double-dipping.',
  },
  {
    id: 'momentum',
    name: 'Momentum',
    category: 'stat',
    description: 'Determines turn order. Every unit accumulates Momentum based on their Speed. At 10,000 Momentum, they take a turn. Higher Momentum cost on an ability means a longer wait before your next turn. Stagger reduces enemy Momentum, delaying their turns.',
  },
  {
    id: 'guard_stat',
    name: 'Guard',
    category: 'stat',
    description: 'Damage reduction from heavy armor that applies to ALL incoming damage (not just physical). Strong against many small hits, weaker against single large hits. Formula: Guard / (Guard + 5 × Damage), capped at 90%. Sources: Mail armor implicits, "Studded" and "Reinforced" prefixes on heavy armor, shield implicits. Fortification mastery adds +2% increased Guard per rank.',
  },
];
